/* eslint-disable prefer-rest-params */
export class Any {}

export const createTuple = function (/* types */) {
  const typeInfo = Array.from(arguments);

  const checkType = (Constr, val) =>
    (val && val.constructor === Constr) || val instanceof Constr;

  class Tuple extends Any {
    constructor(/* values */) {
      super();
      this.values = Array.from(arguments);
      if (this.values.some((val) => val === null || val === undefined)) {
        throw new Error('Tuples may not have any null values');
      }
      if (this.values.length !== typeInfo.length) {
        throw new Error('Tuple arity does not match its prototype');
      }
      this.values.forEach((val, i) => {
        if (!checkType(typeInfo[i], val)) {
          throw new Error(
            `Tuple called with wrong type:` +
              `${val} should be of type ${typeInfo[i]}`
          );
        }
        this[i] = val;
      });
      Object.freeze(this);
    }

    [Symbol.iterator]() {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const tuple = this;
      let i = 0;
      return {
        next() {
          return i < tuple.values.length
            ? { done: false, value: tuple.values[i++] }
            : { done: true };
        },
      };
    }
  }

  return Tuple;
};

export class Maybe extends Any {
  static some(a) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return new Some(a);
  }

  static none() {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return new None();
  }

  static from(a) {
    return a !== null && a !== undefined ? Maybe.some(a) : Maybe.none();
  }

  static lift(fn) {
    return function () {
      const res = fn.apply(fn, arguments);
      return Maybe.from(res);
    };
  }

  get isSome() {
    return false;
  }

  get isNone() {
    return false;
  }
}

export class Some extends Maybe {
  constructor(a) {
    super();
    this.a = a;
  }

  get value() {
    return this.a;
  }

  map(fn) {
    return Maybe.from(fn(this.value));
  }

  getOrElse() {
    return this.value;
  }

  get isSome() {
    return true;
  }

  toString() {
    return `Maybe.Some(${this.value})`;
  }
}

export class None extends Maybe {
  get value() {
    throw new Error('Cannot extract value of an Either.Left');
  }

  map() {
    return this;
  }

  getOrElse(other) {
    return other;
  }

  get isNone() {
    return true;
  }

  toString() {
    return 'Maybe.None';
  }
}

export class Either extends Any {
  static left(e) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return new Left(e);
  }

  static right(a) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return new Right(a);
  }

  static from(a) {
    return a instanceof Error ? Either.left() : Either.right(a);
  }

  static lift(fn) {
    return function () {
      try {
        const res = fn.apply(fn, arguments);
        return Either.from(res);
      } catch (e) {
        return Either.left(e);
      }
    };
  }

  constructor(a) {
    super();
    this.a = a;
  }

  get value() {
    return this.a;
  }
}

export class Left extends Either {
  map() {
    return this;
  }

  get value() {
    throw new Error('Cannot extract value of an Either.Left');
  }

  getOrElse(val) {
    return val;
  }

  toString() {
    return 'Either.Left';
  }
}

export class Right extends Either {
  map(fn) {
    return Either.from(fn(this.value));
  }

  getOrElse() {
    return this.value;
  }

  toString() {
    return `Either.Right(${this.value})`;
  }
}
